import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import type React from "react";
import { useState } from "react";

interface TagsComponentProps {
  tags: Tag[];
  onDeleteTag: (tagId: string) => void;
  defaultVisibleCount?: number;
  title?: string;
  onLastElementDeleted?: () => void;
}

type Tag = {
  tagId: string;
  name: string;
};

export const CollapsibleTagList: React.FC<TagsComponentProps> = ({
  tags,
  defaultVisibleCount = 6,
  onDeleteTag,
  title,
  onLastElementDeleted,
}) => {
  const [visibleTags, setVisibleTags] = useState<Tag[]>(
    tags.slice(0, defaultVisibleCount),
  );
  const [expanded, setExpanded] = useState<boolean>(false);

  const handleToggle = () => {
    setExpanded(!expanded);
    if (!expanded) {
      setVisibleTags(tags);
    } else {
      setVisibleTags(tags.slice(0, defaultVisibleCount));
    }
  };

  const handleDelete = (tagToDelete: Tag) => {
    const updatedTags = visibleTags.filter((tag) => tag !== tagToDelete);
    setVisibleTags(updatedTags);
    updatedTags.length > 0
      ? onDeleteTag(tagToDelete.tagId)
      : onLastElementDeleted?.();
  };

  const remainingTagsCount = tags.length - defaultVisibleCount;

  return (
    <Stack>
      <Typography variant="overline" sx={{ pl: 2 }}>
        {title}
      </Typography>
      <Box
        sx={{
          backgroundColor: "common.grey2",
          padding: 2,
          borderRadius: 2,
          display: "inline-block",
          width: "50%",
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {visibleTags.map((tag, index) => (
            <Chip
              key={index}
              label={tag.name}
              color="secondary"
              onDelete={() => handleDelete(tag)}
            />
          ))}
          {!expanded && remainingTagsCount > 0 && (
            <Typography
              sx={{ alignSelf: "center", marginLeft: 1 }}
              variant="body2"
            >
              + {remainingTagsCount} others...
            </Typography>
          )}
        </Box>
        {tags.length > defaultVisibleCount && (
          <Button onClick={handleToggle} sx={{ mt: 2 }} color="inherit">
            {expanded ? "Show Less" : `Show All (${tags.length})`}
          </Button>
        )}
      </Box>
    </Stack>
  );
};
